import StringUtil from "./stringUtil";

/**
 * HTML上のタグから各種データを取得する処理のUtil。
 */
export default class LoadTagDataUtil {
	/**
	 * 指定されたHTMLElementから`tenantId`を取得。
	 * @param targetElement 指定したHTMLElement
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadTenantId(targetElement?: HTMLElement | null): string | undefined {
		if (!targetElement) {
			console.error(this.errorMessage.notExistElement);
			return undefined;
		}

		const tenantId = targetElement.dataset.revicoTenantid || "";
		if (!tenantId) {
			console.error('revico community talk init error: Add data-revico-tenantid="{tenantId}" to the base script tag.');
			return undefined;
		}

		return tenantId;
	}

	/**
	 * 指定されたHTMLElementから`tagCd`を取得。
	 * @param targetElement 指定したHTMLElement
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadTagCd(targetElement?: HTMLElement | null): string | undefined {
		if (!targetElement) {
			console.error(this.errorMessage.notExistElement);
			return undefined;
		}

		const tagCd = targetElement.dataset.revicoTagcd || "";
		if (!tagCd) {
			console.error('revico community talk init error: Add data-revico-tagcd="{tagCd}" to the base script tag.');
			return undefined;
		}

		return tagCd;
	}

	/**
	 * 指定されたHTMLElementから`communityTalkTagCd`を取得。
	 * @param targetElement 指定したHTMLElement
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadCommunityTalkTagCd(targetElement?: HTMLElement | null): string | undefined {
		if (!targetElement) {
			console.error(this.errorMessage.notExistElement);
			return undefined;
		}

		const communityTalkTagCd = targetElement.dataset.revicoCommunityTalkTagcd || "";
		if (!communityTalkTagCd) {
			console.error('revico community talk init error: Add data-revico-community-talk-tagcd="{communityTalkTagCd}" to the base script tag.');
			return undefined;
		}

		return communityTalkTagCd;
	}

	/**
	 * 指定されたHTMLElement ID名を基に`siteUserId`を取得。
	 * @param targetIdName 指定されたHTMLElement ID名
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadSiteUserId(targetIdName: string): string | undefined {
		const hiddenTag = document.getElementById(targetIdName) as HTMLInputElement;

		if (!hiddenTag) {
			// サイト会員IDは未ログイン時などで普通にElementが存在しないことがあり得るため、エラーとして出力はしない
			return undefined;
		}
		const siteUserId = hiddenTag.value;
		if (!siteUserId) {
			// サイト会員IDは未ログイン時などで普通にElementが存在しないことがあり得るため、エラーとして出力はしない
			return undefined;
		}

		return siteUserId;
	}

	/**
	 * 指定されたHTMLElement ID名を基に`subjectCd`を取得。
	 * @param targetIdName 指定されたHTMLElement ID名
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadSubjectCd(targetIdName: string): string | undefined {
		const hiddenTag = document.getElementById(targetIdName) as HTMLInputElement;
		if (!hiddenTag) {
			console.error('revico community talk init error: Add hidden tag that has id="hidden_subjectcd".');
			return undefined;
		}

		const subjectCd = hiddenTag.value;
		if (!subjectCd) {
			console.error("revico community talk init error: Add hidden tag that has site user subjectCd.");
			return undefined;
		}
		return subjectCd;
	}

	/**
	 * 指定されたHTMLElement ID名を基に`subjectName`を取得。
	 * @param targetIdName 指定されたHTMLElement ID名
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadSubjectName(targetIdName: string): string {
		const hiddenTag = document.getElementById(targetIdName) as HTMLInputElement;
		if (hiddenTag) {
			const subjectName = hiddenTag.value;
			if (!StringUtil.isNullOrEmpty(subjectName)) {
				return subjectName;
			}
		}

		// hiddenタグからSubjectNameが取得できない場合、titleタグから取得
		return document.title;
	}

	/**
	 * 指定されたHTMLElementから`tenantId`を取得。
	 * @param targetElement 指定したHTMLElement
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadSubjectCdByElement(targetElement?: HTMLElement | null): string | undefined {
		if (!targetElement) {
			console.error(this.errorMessage.notExistElement);
			return undefined;
		}

		const subjectCd = targetElement.dataset.revicoSubjectcd || "";
		if (!subjectCd) {
			console.error('revico community talk init error: Add data-revico-subjectcd="{subjectCd}" to the base script tag.');
			return undefined;
		}

		return subjectCd;
	}

	/**
	 * 指定されたHTMLElement ID名を基に`talkRoomUrl`を取得。
	 * @param targetIdName 指定されたHTMLElement ID名
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadTalkRoomUrl(targetIdName: string): string | undefined {
		const hiddenTag = document.getElementById(targetIdName) as HTMLInputElement;
		if (!hiddenTag) {
			// トークルーム表示URLは無くてもエラーにはしない
			return undefined;
		}

		const talkRoomUrl = hiddenTag.value;
		if (!talkRoomUrl) {
			// トークルーム表示URLは無くてもエラーにはしない
			return undefined;
		}
		return talkRoomUrl;
	}

	/**
	 * 指定されたHTMLElement ID名を基に「ニックネーム」を取得。
	 * @param targetIdName 指定されたHTMLElement ID名
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadCommunityTalkUserNickName(targetIdName: string): string | undefined {
		const hiddenTag = document.getElementById(targetIdName) as HTMLInputElement;

		if (!hiddenTag) {
			// ニックネームがなくても動作に影響はないため、エラーとして出力はしない
			return undefined;
		}
		const userNickName = hiddenTag.value;
		if (!userNickName) {
			// ニックネームがなくても動作に影響はないため、エラーとして出力はしない
			return undefined;
		}

		return userNickName;
	}

	private static errorMessage = {
		notExistElement: "revico community talk init error: Add revico-community-talk tag.",
	};

	/**
	 * 指定されたHTMLElement ID名を基に`displayTalkRoomId`を取得。
	 * @param targetIdName 指定されたHTMLElement ID名
	 * @returns 取得結果(取得できなかった場合は`undefined`が返却)
	 */
	static loadDisplayTalkRoomId(targetIdName: string): string | undefined {
		const hiddenTag = document.getElementById(targetIdName) as HTMLInputElement;
		if (!hiddenTag) {
			// 表示用トークルームIDは無くてもエラーにはしない
			return undefined;
		}

		const displayTalkRoomId = hiddenTag.value;
		if (!displayTalkRoomId) {
			// 表示用トークルームIDは無くてもエラーにはしない
			return undefined;
		}
		return displayTalkRoomId;
	}
}
